import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Button,
  Container,
  Divider,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Message,
  Segment,
} from "semantic-ui-react";
import { useQuery } from "@apollo/client";

import { PATHS } from "../../../paths";
import { useIsAdminLoading } from "../../../hooks/isAdmin";
import { GET_PARTNERSHIPS } from "../../../queries/getPartnerships";

// ── Types ────────────────────────────────────────────────────────────────────

type Partnership = {
  id: number;
  supporterOrganisationId: number;
  supporterName: string;
  scope: string;
  scopeLabel?: string | null;
};

type VolunteerRole = { rowId: number; title: string; type: string; icon: string };
type EmploymentCategory = { rowId: number; name: string; shortName: string; count: number };

const VOLUNTEER_TYPES = ["Fellowship", "Project", "Internship"];

// Sample defaults mirror the iOS community details page so a freshly-selected
// relationship isn't empty. These are stub values — nothing is persisted yet.
const SAMPLE_VOLUNTEER_ROLES: Omit<VolunteerRole, "rowId">[] = [
  { title: "Literacy Tutoring", type: "Fellowship", icon: "book.fill" },
  { title: "Sports Coaching", type: "Project", icon: "figure.run" },
  { title: "Community Kitchen", type: "Project", icon: "fork.knife" },
  { title: "Youth Mentoring", type: "Internship", icon: "person.2.fill" },
];

const SAMPLE_EMPLOYMENT: Omit<EmploymentCategory, "rowId">[] = [
  { name: "Textile & Weaving", shortName: "Textile", count: 52 },
  { name: "Crafts & Artisan", shortName: "Crafts", count: 18 },
  { name: "Administrative", shortName: "Admin", count: 12 },
  { name: "Food & Service", shortName: "Service", count: 8 },
  { name: "Other", shortName: "Other", count: 7 },
];

// ── Component ──────────────────────────────────────────────────────────────────

export function Partnerships() {
  const [isAdmin, adminLoading] = useIsAdminLoading();

  const { data, loading } = useQuery(GET_PARTNERSHIPS, { skip: !isAdmin });
  const partnerships: Partnership[] = useMemo(() => data?.partnerships ?? [], [data]);

  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Per-relationship stub state (keyed by partnership id, lazily seeded).
  const [descByRel, setDescByRel] = useState<Record<number, string>>({});
  const [volByRel, setVolByRel] = useState<Record<number, VolunteerRole[]>>({});
  const [empByRel, setEmpByRel] = useState<Record<number, EmploymentCategory[]>>({});
  const [workforceByRel, setWorkforceByRel] = useState<Record<number, number>>({});
  const [savedNote, setSavedNote] = useState(false);

  const rowIdRef = useRef(1);
  const nextRowId = () => rowIdRef.current++;

  // Auto-select when there is exactly one partnership.
  useEffect(() => {
    if (selectedId === null && partnerships.length === 1) {
      setSelectedId(partnerships[0].id);
    }
  }, [partnerships, selectedId]);

  // Seed stub state the first time a relationship is selected.
  useEffect(() => {
    if (selectedId === null) return;
    setVolByRel((prev) =>
      prev[selectedId]
        ? prev
        : { ...prev, [selectedId]: SAMPLE_VOLUNTEER_ROLES.map((r) => ({ ...r, rowId: nextRowId() })) }
    );
    setEmpByRel((prev) =>
      prev[selectedId]
        ? prev
        : { ...prev, [selectedId]: SAMPLE_EMPLOYMENT.map((r) => ({ ...r, rowId: nextRowId() })) }
    );
    setWorkforceByRel((prev) => (prev[selectedId] != null ? prev : { ...prev, [selectedId]: 135 }));
  }, [selectedId]);

  // Gate: non-admins don't belong here. Wait for the admin check so a real admin
  // isn't bounced during the initial load.
  if (!adminLoading && !isAdmin) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const scopeText = (p: Partnership) =>
    p.scope === "*" ? "Whole organisation" : p.scopeLabel || p.scope;

  const options = partnerships.map((p) => ({
    key: p.id,
    value: p.id,
    text: `${p.supporterName} — ${scopeText(p)}`,
  }));

  const selected = partnerships.find((p) => p.id === selectedId) ?? null;
  const desc = selectedId != null ? descByRel[selectedId] ?? "" : "";
  const volunteerRoles = selectedId != null ? volByRel[selectedId] ?? [] : [];
  const employment = selectedId != null ? empByRel[selectedId] ?? [] : [];
  const workforce = selectedId != null ? workforceByRel[selectedId] ?? 0 : 0;
  const totalEmployed = employment.reduce((sum, c) => sum + (Number(c.count) || 0), 0);

  // ── Stub mutators (local state only) ──
  const setDesc = (v: string) =>
    selectedId != null && setDescByRel((p) => ({ ...p, [selectedId]: v }));

  const updateVol = (rowId: number, patch: Partial<VolunteerRole>) =>
    selectedId != null &&
    setVolByRel((p) => ({
      ...p,
      [selectedId]: (p[selectedId] ?? []).map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)),
    }));
  const addVol = () =>
    selectedId != null &&
    setVolByRel((p) => ({
      ...p,
      [selectedId]: [...(p[selectedId] ?? []), { rowId: nextRowId(), title: "", type: VOLUNTEER_TYPES[0], icon: "" }],
    }));
  const removeVol = (rowId: number) =>
    selectedId != null &&
    setVolByRel((p) => ({ ...p, [selectedId]: (p[selectedId] ?? []).filter((r) => r.rowId !== rowId) }));

  const updateEmp = (rowId: number, patch: Partial<EmploymentCategory>) =>
    selectedId != null &&
    setEmpByRel((p) => ({
      ...p,
      [selectedId]: (p[selectedId] ?? []).map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)),
    }));
  const addEmp = () =>
    selectedId != null &&
    setEmpByRel((p) => ({
      ...p,
      [selectedId]: [...(p[selectedId] ?? []), { rowId: nextRowId(), name: "", shortName: "", count: 0 }],
    }));
  const removeEmp = (rowId: number) =>
    selectedId != null &&
    setEmpByRel((p) => ({ ...p, [selectedId]: (p[selectedId] ?? []).filter((r) => r.rowId !== rowId) }));

  const handleSave = () => {
    // TODO: wire to backend mutations once volunteering/employment/description
    // persistence exists. For now this is a UI stub.
    setSavedNote(true);
    setTimeout(() => setSavedNote(false), 2500);
  };

  return (
    <Container style={{ padding: "2em 0em" }}>
      <Header as="h1">Partnerships</Header>
      <p style={{ color: "rgba(0,0,0,0.6)" }}>
        Organisations that support your organisation. Select a relationship to edit the
        volunteering opportunities, employment breakdown, and description for the part of your
        organisation that relationship covers.
      </p>

      {loading && <Segment basic loading style={{ minHeight: 80 }} />}

      {!loading && partnerships.length === 0 && (
        <Message info>
          <Message.Header>No partnerships yet</Message.Header>
          <p>No organisations currently support your organisation.</p>
        </Message>
      )}

      {!loading && partnerships.length > 0 && (
        <>
          <Form>
            <Form.Field>
              <label>Relationship</label>
              <Dropdown
                placeholder="Select a relationship…"
                fluid
                selection
                options={options}
                value={selectedId ?? undefined}
                onChange={(_, { value }) => setSelectedId(value as number)}
              />
            </Form.Field>
          </Form>

          {selected && (
            <div style={{ marginTop: "1.5em" }}>
              <Label color="grey" style={{ marginBottom: "1em" }}>
                <Icon name="handshake outline" />
                {selected.supporterName} · {scopeText(selected)}
              </Label>

              <Message warning size="tiny">
                <Icon name="wrench" />
                These editors are stubbed — changes are held locally and not yet saved to the
                backend.
              </Message>

              {/* ── Description ── */}
              <Segment>
                <Header as="h3">Description</Header>
                <p style={{ color: "rgba(0,0,0,0.6)" }}>
                  Describe the part of your organisation that this relationship's scope (
                  <strong>{scopeText(selected)}</strong>) covers.
                </p>
                <Form>
                  <Form.TextArea
                    rows={4}
                    placeholder="e.g. The weaving cooperative in the northern neighbourhood, serving roughly 135 adults…"
                    value={desc}
                    onChange={(_, { value }) => setDesc(String(value ?? ""))}
                  />
                </Form>
              </Segment>

              {/* ── Volunteering opportunities ── */}
              <Segment>
                <Header as="h3">Volunteering Opportunities</Header>
                {volunteerRoles.length === 0 && (
                  <p style={{ color: "rgba(0,0,0,0.6)" }}>No opportunities yet.</p>
                )}
                {volunteerRoles.map((role) => (
                  <Form key={role.rowId} style={{ marginBottom: "0.75em" }}>
                    <Form.Group>
                      <Form.Input
                        width={7}
                        label="Title"
                        placeholder="e.g. Literacy Tutoring"
                        value={role.title}
                        onChange={(_, { value }) => updateVol(role.rowId, { title: value })}
                      />
                      <Form.Select
                        width={4}
                        label="Type"
                        options={VOLUNTEER_TYPES.map((t) => ({ key: t, value: t, text: t }))}
                        value={role.type}
                        onChange={(_, { value }) => updateVol(role.rowId, { type: String(value) })}
                      />
                      <Form.Input
                        width={4}
                        label="Icon"
                        placeholder="e.g. book.fill"
                        value={role.icon}
                        onChange={(_, { value }) => updateVol(role.rowId, { icon: value })}
                      />
                      <Form.Field width={1}>
                        <label>&nbsp;</label>
                        <Button
                          icon="trash"
                          color="red"
                          basic
                          type="button"
                          onClick={() => removeVol(role.rowId)}
                        />
                      </Form.Field>
                    </Form.Group>
                  </Form>
                ))}
                <Button basic icon="plus" content="Add opportunity" type="button" onClick={addVol} />
              </Segment>

              {/* ── Employment ── */}
              <Segment>
                <Header as="h3">
                  Employment
                  <Label color="green" style={{ marginLeft: "0.75em" }} size="small">
                    {totalEmployed} of {workforce} employed
                  </Label>
                </Header>
                <Form>
                  <Form.Input
                    label="People in workforce (denominator)"
                    type="number"
                    width={5}
                    value={workforce}
                    onChange={(_, { value }) =>
                      selectedId != null &&
                      setWorkforceByRel((p) => ({ ...p, [selectedId]: Number(value) || 0 }))
                    }
                  />
                </Form>
                <Divider />
                {employment.length === 0 && (
                  <p style={{ color: "rgba(0,0,0,0.6)" }}>No categories yet.</p>
                )}
                {employment.map((cat) => (
                  <Form key={cat.rowId} style={{ marginBottom: "0.75em" }}>
                    <Form.Group>
                      <Form.Input
                        width={7}
                        label="Category"
                        placeholder="e.g. Textile & Weaving"
                        value={cat.name}
                        onChange={(_, { value }) => updateEmp(cat.rowId, { name: value })}
                      />
                      <Form.Input
                        width={4}
                        label="Short name"
                        placeholder="e.g. Textile"
                        value={cat.shortName}
                        onChange={(_, { value }) => updateEmp(cat.rowId, { shortName: value })}
                      />
                      <Form.Input
                        width={4}
                        label="Count"
                        type="number"
                        value={cat.count}
                        onChange={(_, { value }) =>
                          updateEmp(cat.rowId, { count: Number(value) || 0 })
                        }
                      />
                      <Form.Field width={1}>
                        <label>&nbsp;</label>
                        <Button
                          icon="trash"
                          color="red"
                          basic
                          type="button"
                          onClick={() => removeEmp(cat.rowId)}
                        />
                      </Form.Field>
                    </Form.Group>
                  </Form>
                ))}
                <Button basic icon="plus" content="Add category" type="button" onClick={addEmp} />
              </Segment>

              <Button primary icon="save" content="Save changes" type="button" onClick={handleSave} />
              {savedNote && (
                <Message positive size="tiny" style={{ marginTop: "1em" }}>
                  Captured locally (stub) — backend persistence is not wired up yet.
                </Message>
              )}
            </div>
          )}
        </>
      )}
    </Container>
  );
}
